'use client';

import React, { useEffect, useRef, useState } from 'react';

// Enter and exit animations in CSS, in place of the motion library.
//
// Motion was 42 KB of JavaScript on every public page - the navbar, the cookie
// banner, the compare bar and the card buttons all imported it - and it cost
// 0.5-0.8 s of script evaluation on a mid-range phone before the page could
// respond. Every use outside the filter sheet's drag gesture was a fade, a
// slide or a scale in and out, which CSS transitions do on the compositor
// thread for nothing.
//
// Usage mirrors AnimatePresence: the child stays mounted while it animates
// out, and is removed afterwards.
//
//   <Transition show={open} from={{ opacity: 0, y: 8, scale: 0.98 }} duration={150}>
//     ...
//   </Transition>
//
// Content already showing on the first render does not animate in: fading
// server-rendered content in from nothing delays the Largest Contentful Paint
// for no gain. Pass `appear` for something mounted later that should.

export type Pose = { opacity?: number; x?: number | string; y?: number | string; scale?: number };

type Props = Omit<React.HTMLAttributes<HTMLElement>, 'style'> & {
  show: boolean;
  from?: Pose;
  /** Where it goes on the way out; defaults to `from`. */
  exit?: Pose;
  duration?: number;
  /** 'spring' approximates motion's springs with a slight overshoot. */
  easing?: 'ease-out' | 'spring' | 'linear';
  /** Animate in even if showing on the first render. */
  appear?: boolean;
  /** Animate the height between 0 and the content's own height. */
  collapse?: boolean;
  as?: 'div' | 'span' | 'aside' | 'section';
  style?: React.CSSProperties;
  children?: React.ReactNode;
};

const EASING = {
  'ease-out': 'cubic-bezier(0.16, 1, 0.3, 1)',
  spring: 'cubic-bezier(0.34, 1.4, 0.64, 1)',
  linear: 'linear',
};

const unit = (v: number | string) => (typeof v === 'number' ? `${v}px` : v);

function toStyle(pose: Pose | null): React.CSSProperties {
  if (!pose) return { opacity: 1, transform: 'none' };
  const parts: string[] = [];
  if (pose.x !== undefined || pose.y !== undefined) parts.push(`translate(${unit(pose.x ?? 0)}, ${unit(pose.y ?? 0)})`);
  if (pose.scale !== undefined) parts.push(`scale(${pose.scale})`);
  return { opacity: pose.opacity ?? 1, transform: parts.length ? parts.join(' ') : 'none' };
}

type Phase = 'hidden' | 'entering' | 'shown' | 'leaving';

export default function Transition({
  show,
  from = { opacity: 0 },
  exit,
  duration = 150,
  easing = 'ease-out',
  appear = false,
  collapse = false,
  as = 'div',
  style,
  children,
  ...rest
}: Props) {
  const [phase, setPhase] = useState<Phase>(show ? (appear ? 'entering' : 'shown') : 'hidden');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (show) {
      if (phase === 'shown') return;
      setPhase('entering');
      // Two frames: one to paint the starting pose, one to start moving.
      let second = 0;
      const first = requestAnimationFrame(() => {
        second = requestAnimationFrame(() => setPhase('shown'));
      });
      return () => {
        cancelAnimationFrame(first);
        cancelAnimationFrame(second);
      };
    }
    if (phase === 'hidden') return;
    setPhase('leaving');
    timer.current = setTimeout(() => setPhase('hidden'), duration);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // phase is read, not followed: only a change of `show` starts a transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, duration]);

  if (phase === 'hidden') return null;

  const pose = phase === 'shown' ? null : phase === 'leaving' ? exit ?? from : from;
  const ease = EASING[easing];
  const transition = `opacity ${duration}ms ${ease}, transform ${duration}ms ${ease}`;
  const Tag = as;

  if (collapse) {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateRows: phase === 'shown' ? '1fr' : '0fr',
          transition: `grid-template-rows ${duration}ms ${ease}`,
        }}
      >
        <Tag {...rest} style={{ ...style, ...toStyle(pose), overflow: 'hidden', minHeight: 0, transition }}>
          {children}
        </Tag>
      </div>
    );
  }

  return (
    <Tag {...rest} style={{ ...style, ...toStyle(pose), transition }}>
      {children}
    </Tag>
  );
}
